import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  FileText,
  Upload,
  Clock,
  CheckCircle2,
  AlertCircle,
  LogOut,
  Home,
  User,
  ChevronRight,
  Phone,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOAN_STAGES = [
  "Application",
  "Document Verification",
  "Bank Submission",
  "Sanctioned",
  "Disbursed",
  "Closed"
];

const stageColors = {
  'Application': { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-500' },
  'Document Verification': { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-500' },
  'Bank Submission': { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-500' },
  'Sanctioned': { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-500' },
  'Disbursed': { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-500' },
  'Closed': { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-500' }
};

const PortalDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loans, setLoans] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('portal_token');
    const storedUser = localStorage.getItem('portal_user');
    
    if (!token) {
      navigate('/portal/login');
      return;
    }
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [loansRes, docsRes] = await Promise.all([
        axios.get(`${API}/portal/my-loans`),
        axios.get(`${API}/portal/documents`)
      ]);
      setLoans(loansRes.data);
      setDocuments(docsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_user');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/portal/login');
  };

  const getStageProgress = (stage) => {
    const index = LOAN_STAGES.indexOf(stage);
    return ((index + 1) / LOAN_STAGES.length) * 100;
  };

  const formatCurrency = (amount) => `₹${amount?.toLocaleString('en-IN') || 0}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-brand-navy rounded-lg flex items-center justify-center">
                <span className="text-xl font-bold text-brand-gold font-heading">A</span>
              </div>
              <div>
                <h1 className="font-bold text-brand-navy font-heading">Customer Portal</h1>
                <p className="text-xs text-slate-500">Atharva Finance Solutions</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <Home className="h-4 w-4 mr-2" />
                  Home
                </Button>
              </Link>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg">
                <User className="h-4 w-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">{user?.name}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                data-testid="portal-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-brand-navy font-heading">
            Welcome, {user?.name}!
          </h2>
          <p className="text-slate-500">Track your loan applications and manage documents</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Applications</p>
                  <p className="text-2xl font-bold text-brand-navy">{loans.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">In Progress</p>
                  <p className="text-2xl font-bold text-brand-navy">
                    {loans.filter(l => !['Disbursed', 'Closed'].includes(l.stage)).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Completed</p>
                  <p className="text-2xl font-bold text-brand-navy">
                    {loans.filter(l => ['Disbursed', 'Closed'].includes(l.stage)).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Loan Applications */}
        <Card className="border-0 shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-brand-navy">
              Your Loan Applications
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loans.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No loan applications found</p>
                <p className="text-sm text-slate-400">
                  Your loan applications will appear here once submitted by our team.
                </p>
                <Link to="/#contact">
                  <Button className="mt-4 bg-brand-navy">
                    Apply for Loan
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {loans.map((loan) => (
                  <div
                    key={loan.id}
                    data-testid={`loan-item-${loan.id}`}
                    className={`p-6 bg-white border-l-4 ${stageColors[loan.stage]?.border || 'border-slate-500'} rounded-lg shadow-sm`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-sm font-semibold text-brand-navy">
                            {loan.file_number}
                          </span>
                          <Badge className={`${stageColors[loan.stage]?.bg} ${stageColors[loan.stage]?.text}`}>
                            {loan.stage}
                          </Badge>
                        </div>
                        <p className="text-slate-600">{loan.loan_type}</p>
                        <p className="text-2xl font-bold text-brand-navy mt-1">
                          {formatCurrency(loan.loan_amount)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400 mb-1">Applied on</p>
                        <p className="text-sm text-slate-600">
                          {new Date(loan.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-4">
                      <div className="flex justify-between text-xs text-slate-500 mb-2">
                        <span>Progress</span>
                        <span>{Math.round(getStageProgress(loan.stage))}%</span>
                      </div>
                      <Progress value={getStageProgress(loan.stage)} className="h-2" />
                    </div>

                    {/* Stage Timeline */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {LOAN_STAGES.map((stage, index) => {
                        const currentIndex = LOAN_STAGES.indexOf(loan.stage);
                        const isCompleted = index <= currentIndex;
                        const isCurrent = index === currentIndex;
                        
                        return (
                          <div
                            key={stage}
                            className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                              isCompleted
                                ? isCurrent
                                  ? 'bg-brand-navy text-white'
                                  : 'bg-emerald-100 text-emerald-700'
                                : 'bg-slate-100 text-slate-400'
                            }`}
                          >
                            {isCompleted && !isCurrent && <CheckCircle2 className="h-3 w-3" />}
                            {isCurrent && <Clock className="h-3 w-3" />}
                            {stage}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Documents Section */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-heading text-brand-navy">
              Your Documents
            </CardTitle>
            <Link to="/portal/documents">
              <Button variant="outline" size="sm">
                <Upload className="h-4 w-4 mr-2" />
                Upload Document
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {documents.length === 0 ? (
              <div className="text-center py-8">
                <Upload className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No documents uploaded yet</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="p-4 bg-slate-50 rounded-lg border border-slate-100"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="h-8 w-8 text-brand-navy" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-brand-navy truncate">{doc.document_name}</p>
                        <p className="text-xs text-slate-500">{doc.document_type}</p>
                      </div>
                      <Badge className={
                        doc.status === 'verified' ? 'bg-emerald-100 text-emerald-700' :
                        doc.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        {doc.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contact Help */}
        <Card className="border-0 shadow-lg bg-brand-navy text-white mt-8">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-bold font-heading text-lg mb-1">Need Help?</h3>
                <p className="text-slate-300 text-sm">Contact our team for any queries</p>
              </div>
              <div className="flex items-center gap-4">
                <a href="tel:7410001036" className="flex items-center gap-2 text-brand-gold">
                  <Phone className="h-5 w-5" />
                  <span className="font-semibold">7410001036</span>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PortalDashboard;
